import type { DragEndEvent } from '@dnd-kit/core';
import type { ProposalHeading } from '../../types/proposal';

export const useStructureMutations = (
  setProposalStructure: React.Dispatch<React.SetStateAction<ProposalHeading[]>>
) => {
  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over, delta } = event;

    if (over && active.id !== over.id) {
      setProposalStructure((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
          return items;
        }

        const activeItem = items[oldIndex];
        if (!activeItem) {
          return items;
        }

        const activeLevel = activeItem.level || 1;

        let childrenCount = 0;
        for (let i = oldIndex + 1; i < items.length; i++) {
          const nextItem = items[i];
          if (!nextItem) {
            break;
          }

          if ((nextItem.level || 1) > activeLevel) {
            childrenCount++;
          } else {
            break;
          }
        }

        if (newIndex >= oldIndex && newIndex <= oldIndex + childrenCount) {
          return items;
        }

        const newItems = [...items];
        const block = newItems.splice(oldIndex, childrenCount + 1);

        let insertIndex = newIndex;
        if (oldIndex < newIndex) {
          insertIndex = newIndex - block.length + 1;
        }

        newItems.splice(insertIndex, 0, ...block);
        return newItems;
      });
    }

    const indentThreshold = 24;
    if (typeof delta?.x === 'number' && Math.abs(delta.x) >= indentThreshold) {
      setProposalStructure((items) => {
        const index = items.findIndex((i) => i.id === active.id);
        if (index === -1) {
          return items;
        }

        const currentItem = items[index];
        if (!currentItem) {
          return items;
        }

        const currentLevel = currentItem.level || 1;

        let nextLevel: number;
        if (delta.x > 0) {
          nextLevel = Math.min(4, currentLevel + 1);
        } else {
          nextLevel = Math.max(1, currentLevel - 1);
        }

        if (nextLevel === currentLevel) {
          return items;
        }

        const levelDiff = nextLevel - currentLevel;
        const updatedItems = [...items];

        updatedItems[index] = {
          ...currentItem,
          level: nextLevel as 1 | 2 | 3 | 4,
        };

        for (let i = index + 1; i < updatedItems.length; i++) {
          const child = updatedItems[i];
          if (!child) {
            break;
          }

          const childLevel = child.level || 1;

          if (childLevel <= currentLevel) {
            break;
          }

          const newChildLevel = Math.max(1, Math.min(4, childLevel + levelDiff));
          updatedItems[i] = {
            ...child,
            level: newChildLevel as 1 | 2 | 3 | 4,
          };
        }

        return updatedItems;
      });
    }
  };

  const handleHeadingUpdate = (id: string, newTitle: string): void => {
    setProposalStructure((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          return { ...item, title: newTitle };
        }
        return item;
      })
    );
  };

  const handleFocuserUpdate = (id: string, newFocuser: string): void => {
    setProposalStructure((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          return { ...item, focuser: newFocuser };
        }
        return item;
      })
    );
  };

  const removeHeading = (index: number): void => {
    setProposalStructure((currentStructure) => currentStructure.filter((_, i) => i !== index));
  };

  return {
    handleDragEnd,
    handleHeadingUpdate,
    handleFocuserUpdate,
    removeHeading,
  };
};
